"use client";

import React, { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import ReCAPTCHA from "react-google-recaptcha";
import GoogleLoginButton from "@components/SocialAuth/GoogleLoginButton";
import { oauthRequestFields } from "@components/SocialAuth/oauthFields";
import { Post } from "src/services/ClientSideAPIService";
import {
  addTimeInMinutes,
  findSiteNameWithoutCom,
  getUTMParams,
  readCookie,
  setCookie,
  SiteName_SiteID_Map,
  validateEmailFormat,
} from "helpers/Helper";
import browser from "src/utils/browser";
import { useAppContext } from "@components/Provider";
import { isEbbLandingPageType } from "./ebbLandingPageUtils";
import styles from "./EBBLandingPage.module.css";

const EBBLandingSignupForm = ({
  variant = "hero",
  formId,
  signUpLocation = "",
  newsletterProps = {},
  Newsletter_Settings,
  postType,
  layoutClass,
}) => {
  const router = useRouter();
  const { siteId, li_did } = useAppContext();
  const recaptchaRef = useRef(null);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const signupLocation = signUpLocation || newsletterProps.signUpLocation || "EBB-primary-funnel";
  const recaptchaEnabled = process.env.GOOGLE_RECAPTCHA_ENABLE == "true";

  const dealsRedirect = () => {
    const thankYou = newsletterProps?.thankYou || {};
    const path = thankYou.queryString
      ? `${thankYou.redirectUrl}?${thankYou.queryString}`
      : thankYou.redirectUrl;
    return path ? `/${String(path).replace(/^\/+/, "")}` : "";
  };

  const readUtmCookie = () => {
    try {
      const raw = readCookie("utm_params");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  };

  const buildSignupParams = (emailValue, extra = {}) => {
    const queryString = typeof window !== "undefined" ? window.location.search : "";
    const utmParams = getUTMParams(queryString) || readUtmCookie();
    const newsletterName = findSiteNameWithoutCom(siteId);
    const lists = {};
    const primaryLists = newsletterProps.primaryNewsletter;
    if (Array.isArray(primaryLists) && primaryLists.length > 0) {
      primaryLists.forEach((listName) => {
        lists[listName] = 1;
      });
    }

    const vars = {
      signup_location: signupLocation,
      giveaway_source: [],
      slug: browser.getHostname(),
      referrer: browser.getReferrer(),
      partner_checkbox: "",
      email_frequency: "Default",
      ...utmParams,
      ...(newsletterProps.additionalVars || {}),
    };
    if (!newsletterProps.skipPreferenceCenter && signupLocation !== "Header") {
      vars.schedule_time = addTimeInMinutes(
        process.env.SCHEDULE_WELCOME_NEWSLETTER_TIME_IN_MINUTES
      );
    }

    const userOptionalParams = {
      site_id: SiteName_SiteID_Map(newsletterName),
      vars,
    };
    if (Object.keys(lists).length > 0) {
      userOptionalParams.lists = lists;
    }

    return {
      email: emailValue,
      li_did: li_did || "",
      newsletters: [
        {
          name: newsletterName,
          shouldSendEmail: true,
          sailthruEmailTemplate: newsletterProps.welcomeEmailTemplate,
          userOptionalParams,
        },
      ],
      ...extra,
    };
  };

  const trackSubmit = (submittedEmail) => {
    window.uetq = window.uetq || [];
    window.uetq.push({
      ec: "BingAd",
      ea: "NewsletterSignup",
      el: "PrimaryFunnel",
      ev: 1,
    });
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "newsletter-component-submission",
        signupLocation,
        email: submittedEmail,
      });
    }
  };

  const handleSuccess = (response, submittedEmail) => {
    const { res, token } = response;
    const { userAlreadyExist, email: signedEmail } = res?.MongoResponse?.info || {};
    const trackedEmail = signedEmail || submittedEmail;
    const redirectUrl = dealsRedirect();

    if (userAlreadyExist) {
      if (trackedEmail) {
        setCookie("subscribed", trackedEmail, process.env.SUBSCRIBED_COOKIE_EXPIRY_DAYS);
      }
      if (isEbbLandingPageType(postType) && redirectUrl) {
        setErrorMsg("You are already subscribed. Redirecting to today's deals!");
        setLoading(false);
        setGoogleLoading(false);
        window.setTimeout(() => {
          window.location.href = redirectUrl;
        }, 3000);
        return;
      }
      setErrorMsg("You are already subscribed. Check your inbox for our daily emails.");
      setLoading(false);
      setGoogleLoading(false);
      return;
    }

    if (trackedEmail) {
      setCookie("subscribed", trackedEmail, process.env.SUBSCRIBED_COOKIE_EXPIRY_DAYS);
    }
    if (token) {
      setCookie("ormtoken", token, process.env.SUBSCRIBED_COOKIE_EXPIRY_DAYS);
      setCookie("wasloggedin", true, process.env.SUBSCRIBED_COOKIE_EXPIRY_DAYS);
    }
    if (!Newsletter_Settings || Newsletter_Settings.enabled) {
      trackSubmit(trackedEmail);
    }
    if (!newsletterProps.skipPreferenceCenter && signupLocation !== "Header") {
      router.replace("/account/welcome");
      return;
    }
    if (redirectUrl) {
      window.location.href = redirectUrl;
      return;
    }
    setErrorMsg(newsletterProps?.thankYou?.message || "You are subscribed successfully.");
    setLoading(false);
    setGoogleLoading(false);
  };

  const handleError = () => {
    setLoading(false);
    setGoogleLoading(false);
    setErrorMsg("Signup failed. Please try again.");
    recaptchaRef.current?.reset?.();
  };

  const verifyCaptcha = async () => {
    if (!recaptchaEnabled || !recaptchaRef.current) return true;
    const captchaToken = await recaptchaRef.current.executeAsync();
    const captchaVerification =
      (await Post("gverify", { data: { gToken: captchaToken }, siteId }, null, () => {})) || "";
    recaptchaRef.current.reset();
    return captchaVerification && captchaVerification.status === 200;
  };

  const submitSignup = async (overrideEmail, extra = {}, isGoogle = false) => {
    const emailValue = (overrideEmail || email || "").trim();
    if (!validateEmailFormat(emailValue)) {
      setErrorMsg("Please provide a valid email address");
      if (isGoogle) setGoogleLoading(false);
      return;
    }

    setErrorMsg("");
    if (isGoogle) setGoogleLoading(true);
    else setLoading(true);

    if (!isGoogle) {
      const captchaOk = await verifyCaptcha();
      if (!captchaOk) {
        setLoading(false);
        setErrorMsg("Something went wrong with ReCaptcha validation. Please try again later.");
        return;
      }
    }

    await Post(
      "SignUp",
      { data: buildSignupParams(emailValue, extra), siteId },
      (response) => handleSuccess(response, emailValue),
      handleError
    );
  };

  const onGoogleSuccess = (userData) => {
    const { email: googleEmail, ...extra } = oauthRequestFields(userData);
    submitSignup(googleEmail, extra, true);
  };

  return (
    <div
      className={`${styles.signupForm} ${styles[`signupForm_${variant}`]} ${
        layoutClass ? styles[layoutClass] : ""
      }`}
      id={formId}
    >
      {errorMsg ? <div className={styles.signupError}>{errorMsg}</div> : null}
      <form
        className={styles.emailRow}
        onSubmit={(e) => {
          e.preventDefault();
          submitSignup();
        }}
      >
        <label className={styles.srOnly} htmlFor={`${formId}-email`}>
          Email address
        </label>
        <input
          id={`${formId}-email`}
          type="email"
          autoComplete="email"
          placeholder="Enter your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={styles.emailInput}
        />
        <button
          type="submit"
          className={styles.startReadingBtn}
          disabled={loading}
          aria-busy={loading}
        >
          {loading ? "SUBSCRIBING..." : "START READING"}
        </button>
      </form>
      {recaptchaEnabled ? (
        <ReCAPTCHA
          style={{ display: "none" }}
          ref={recaptchaRef}
          size="invisible"
          sitekey={process.env.GOOGLE_RECAPTCHA_SITEKEY}
          badge="inline"
        />
      ) : null}
      <div className={styles.orDivider}>
        <span>– OR –</span>
      </div>
      <div className={styles.googleWrap}>
        <GoogleLoginButton
          loading={googleLoading}
          onSuccess={onGoogleSuccess}
          onError={(msg) => {
            if (msg !== "access_denied") {
              setErrorMsg(msg || "Google signup failed. Please try again.");
            }
            setGoogleLoading(false);
          }}
          loadingText="Subscribing..."
          buttonText="Continue with Google"
        />
      </div>
    </div>
  );
};

export default EBBLandingSignupForm;
